import { motion } from "motion/react";
import { Loader2, RotateCcw, SquareArrowOutUpRight } from "lucide-react";

import { type AddressLight } from "shared/apiClient";
import { getText, interpolate, type LocalisationConfig } from "shared/lib";
import { animationVariants } from "@/components/wizard/widgets/lib";

import { useCancelOrCompleteApplication } from "@/hooks";

import { Button, CallOut } from "shared/components";
import { SiteOverview } from "@/components/siteOverview";

export interface AddressOutOfScopeProps {
  address: AddressLight | undefined | null;
  localisationConfig: LocalisationConfig | undefined | null;
}

export function AddressOutOfScope({
  address,
  localisationConfig,
}: AddressOutOfScopeProps) {
  const {
    isPending: cancelOrCompleteApplicationLoading,
    cancelOrCompleteApplication,
  } = useCancelOrCompleteApplication({});

  const handleClickChangeAddress = () => {
    console.log("<AddressOutOfScope> handleClickChangeAddress()");
    cancelOrCompleteApplication();
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex flex-col items-end gap-2 md:mt-8"
      >
        <div className="flex gap-2">
          <Button
            disabled={cancelOrCompleteApplicationLoading}
            onClick={handleClickChangeAddress}
            size="sm"
            variant="outline"
          >
            {cancelOrCompleteApplicationLoading ? (
              <>
                <Loader2 className="animate-spin" data-testid="submitting" />
              </>
            ) : null}
            {getText("Change address")}
            <RotateCcw />
          </Button>

          <a
            className="text-headingmd bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-2 font-normal"
            href={localisationConfig?.["councilContactLink"]}
            rel="noreferrer"
            tabIndex={0}
            target="_blank"
          >
            {getText("Talk to a planner")}
            <SquareArrowOutUpRight className="h-4 w-4" />
          </a>
        </div>
      </motion.div>
    );
  };

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-5 text-bodylg md:max-w-[600px]"
      data-testid="step-container-multi-select"
    >
      <div className="flex flex-col gap-3 md:gap-5">
        <h2 className="text-headingxl md:text-heading2xl text-textPrimary">
          {getText("🏡 Your address isn't covered by this service yet")}
        </h2>

        <CallOut className="border-interfaceBorder bg-interfaceBgDarkPaper text-bodylg">
          {interpolate(
            "Your address is affected by a zone or overlay that isn't covered by this service. We're continually expanding the service so it covers more addresses, but for now, please {{link}} to discuss your plans.",
            {
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover inline-block items-center gap-1 underline"
                  href={localisationConfig?.["councilContactLink"]}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("talk to a planner")}{" "}
                  <SquareArrowOutUpRight className="inline-block h-4 w-4" />
                </a>
              ),
            },
          )}
        </CallOut>
      </div>

      <SiteOverview address={address} />

      {renderFooter()}
    </div>
  );
}
